// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

import { Environment } from 'src/environments';
import { logRequestResponse } from './../lib/ApplicationInsights';
import CorrelationVector from './../lib/CorrelationVector';
import { newGuid } from './../lib/uuid';
import IHeaders from 'Types/Headers';
import i18n from 'i18next';
import { msalInstance } from 'src/index';
import { RedirectAuthRequest, SilentAuthRequest } from './authConfig';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';

export const HTTP_RESPONSE_CHANNEL = 'HTTP_RESPONSE';

export type Request = {
    body?: object;
    /* What version of the FD to use, either v2 or v3 */
    headers?: IHeaders;
    requestUrl: string;
    type: string;
};

export interface ProxiedApiCallOptions {
    defaultErrorMessage?: string;
}

export interface HttpResponseChannelMessage {
    request: Request;
    response: AxiosResponse;
}

async function getToken(forceRefresh: boolean = false) {
    const accounts = msalInstance.getAllAccounts();
    let token: AuthenticationResult | null = null;
    if (accounts.length > 0) {
        const silentRequest = SilentAuthRequest;
        silentRequest.account = accounts[0];
        silentRequest.forceRefresh = forceRefresh;

        const redirectRequest = RedirectAuthRequest;
        redirectRequest.loginHint = accounts[0].username;

        await msalInstance.acquireTokenSilent(silentRequest).then(tokenResponse => {
            token = tokenResponse;
        }).catch(error => {
            if (error instanceof InteractionRequiredAuthError) {
                // fallback to interaction when silent call fails
                return msalInstance.acquireTokenRedirect(redirectRequest);
            }
            return msalInstance.loginRedirect(redirectRequest);
        });
    }
    return token;
}

function doesExpiryDateHasMilliseconds(expiryDate: any): boolean {
    let expiryTime = new Date(expiryDate).getTime();

    if (expiryTime >= 1e10) {
        return true;
    } else {
        return false;
    }
}


function isTokenExpired(expiryDate: any): boolean {
    if (expiryDate) {
        let targetDateTime = new Date(expiryDate).getTime();
        const currDateTime = new Date().getTime();
        if (!doesExpiryDateHasMilliseconds(expiryDate)) {
            targetDateTime *= 1000;
        }
        const buffer = 60 * 1000; // 1 minute in milliseconds 
        const decision = currDateTime > (targetDateTime - buffer); // Adding buffer to avoid any edge case issues
        return decision;
    }
    return true;
}


export default {
    async proxiedApiCall(request: Request, options: ProxiedApiCallOptions = {}) {
        if (window.location.search.toUpperCase().includes('LOGINONLY')) {
            return;
        }
        const selectedLanguage = i18n.language;
        let token: AuthenticationResult | null = await getToken();
        //picking up expiry from token instead of expiresOn
        if (!token || token === null || isTokenExpired(token['idTokenClaims']['exp'])) {
            token = await getToken(true);
        }
           // Ensure we have a valid token after refresh attempt
        if (!token || !token['idToken']) {
            throw new Error('Unable to acquire authentication token');
        } 
        else {
            const authToken = token['idToken'];
            let logRequest: any = undefined;
            let logResponse: any = undefined;
            let isError = false;
            try {
                const cv = CorrelationVector.getCV();
                logRequest = {
                    baseURL: Environment.getEnvironment.hostname,
                    data: request.body,
                    headers: {
                        ...request.headers, // Any pass-through headers have to be added/recognized manually on server's api endpoint listener
                        'content-type': 'application/json',
                        authorization: `Bearer ${authToken}`,
                        'cache-control': 'no-store', // cache should not store anything about the client request or server response
                        'ms-correlationid': newGuid().toLowerCase(),
                        'ms-cv': cv,
                        'request-id': cv,
                        'language': selectedLanguage ? selectedLanguage : 'en',
                    },
                    method: request.type,
                    url: request.requestUrl,
                } as AxiosRequestConfig;

                const response = await axios(logRequest);
                logResponse = response;
                return response && response.data;
            } catch (error) {
                isError = true;
                // See https://gist.github.com/fgilio/230ccd514e9381fafa51608fcf137253 for fine-tuned error handling
                // Must shallow copy before clone else clone will result in an empty object
                logResponse = {
                    ...error,
                };
                console.error(error);
               throw error;
            } finally {
                // Put timeout before logging so a failure to get telemetry out doesn't cause this request to fail
                setTimeout(() => {
                    logRequestResponse(logRequest, logResponse, isError);
                }, 100);
            }
        }
        return undefined;
    },
};
